import React, { useState, useEffect } from "react";
import { Field, Form, FieldError } from "react-jsonschema-form-validation";
import {
	fromUnixTime,
	millisecondsToSeconds,
	secondsToMilliseconds,
	getYear,
	getDaysInMonth,
	getDate,
	getMonth,
	getUnixTime,
} from "date-fns";
import _ from "lodash";
import { getMonthAbbreviation } from "../services/dateService";
import { Select } from "./common/Select";
import { SimpleSelect } from "./common/SimpleSelect";
import { sanitize } from "dompurify";
import { FormValidator } from "./common/FormValidator";
import phone from "phone";
import { getList } from "../services/dashApiService";
import { toast } from "react-toastify";
import { QuestionnaireHeader } from "./QuestionnaireHeader";
import { QuestionnaireFooter } from "./QuestionnaireFooter";

const areEqual = (prevProps, nextProps) => {
	if (
		prevProps.def === nextProps.def &&
		prevProps.currentResponse === nextProps.currentResponse
	) {
		return true;
	} else {
		return false;
	}
};

export const QuestionnairePid = React.memo(
	({ def, onRespond, currentResponse }) => {
		const sexOptions = [
			{
				label: "Male",
				value: "Male",
				id: "male",
			},
			{
				label: "Female",
				value: "Female",
				id: "female",
			},
			{
				label: "Prefer Not to Say",
				value: "PreferNotToSay",
				id: "preferNotToSay",
			},
		];

		const { value: data } = currentResponse || {};
		const schema = {
			type: "object",
			properties: {
				firstName: { type: "string", maxLength: 64 },
				surname: { type: "string", maxLength: 64 },
				postalCode: { type: "string", minLength: 6, maxLength: 8 },
				cancerAlliance: { type: "string", maxLength: 64 },
				dob: { type: "number" },
			},
			required: [
				"firstName",
				"surname",
				"sex",
				"postalCode",
				// "dob",
				"cancerAlliance",
			],
		};

		if (def.showCancer) {
			schema.properties.diseaseGroup = { type: "string", maxLength: 64 };
			schema.required.push("diseaseGroup");
		}

		const formFieldsDefault = {
			firstName: (data && data.firstName) ?? "",
			surname: (data && data.surname) ?? "",
			sex: (data && data.sex) ?? "PreferNotToSay",
			mobilePhone: (data && data.mobilePhone) ?? "",
			postalCode: (data && data.postalCode) ?? "",
			cancerAlliance: (data && data.cancerAlliance) ?? "",
			dob: data && data.dob ? data.dob : new Date("1970-01-01 12:00"),
		};

		if (def.showCancer) {
			formFieldsDefault.diseaseGroup = (data && data.diseaseGroup) ?? "";
			formFieldsDefault.cancerName = (data && data.cancerName) ?? "";
		}

		const [formData, setFormData] = useState(formFieldsDefault);
		const [cancerAlliances, setCancerAlliances] = useState([]);
		const [diseaseGroups, setDiseaseGroups] = useState([]);
		const [validatorIsValid, setValidatorIsValid] = useState(false);

		const submitResponse = (data) => {
			const response = {
				id: def.id,
				value: {
					...data,
				},
				valid: validatorIsValid,
			};

			onRespond(response);
		};

		useEffect(() => {
			if (data) {
				setFormData(data);
			} else {
				const _selectedOption = { ...formFieldsDefault };

				if (def.answer) {
					_selectedOption.firstName =
						def.answer.firstName || _selectedOption.firstName;
					_selectedOption.surname =
						def.answer.surname || _selectedOption.surname;
					_selectedOption.sex = def.answer.sex || _selectedOption.sex;
					_selectedOption.mobilePhone =
						def.answer.mobilePhone || _selectedOption.mobilePhone;
					_selectedOption.postalCode =
						def.answer.postalCode || _selectedOption.postalCode;
					_selectedOption.cancerAlliance =
						def.answer.cancerAlliance || _selectedOption.cancerAlliance;
					_selectedOption.dob = def.answer.dob || _selectedOption.dob;

					if (def.showCancer) {
						_selectedOption.diseaseGroup =
							def.answer.diseaseGroup || _selectedOption.diseaseGroup;
					}
				}

				setFormData(_selectedOption);

				submitResponse(_selectedOption);
			}
		}, [data, def, onRespond]);

		const setFormField = (value, propertyName) => {
			let updatedData = { ...formData };
			updatedData[propertyName] = value;
			setFormData(updatedData);
			submitResponse(updatedData);
		};

		const years = _.range(1900, getYear(Date.now()) + 1);
		const yearsOptions = years.map((y) => {
			return {
				id: y,
				label: y,
				value: y,
			};
		});

		const months = _.range(12);
		const monthsOptions = months.map((m) => {
			return {
				id: m,
				label: getMonthAbbreviation(m),
				value: m,
				key: m,
			};
		});

		const [daysInMonth, setDaysInMonth] = useState(
			getDaysInMonth(
				fromUnixTime(
					millisecondsToSeconds(
						data ? data.dob : new Date("1970-01-01 12:00").valueOf()
					)
				)
			)
		);

		const days = _.range(1, daysInMonth + 1);
		const dayOptions = days.map((d) => {
			return {
				id: d,
				label: d,
				value: d,
				key: d,
			};
		});

		const [dateOfBirth, setDateOfBirth] = useState(
			fromUnixTime(
				millisecondsToSeconds(
					data ? data.dob : new Date("1970-01-01 12:00").valueOf()
				)
			)
		);

		const initialDate = dayOptions.find(
			(date) => date.value === getDate(dateOfBirth)
		);
		const initialMonth = monthsOptions.find(
			(month) => month.value === getMonth(dateOfBirth)
		);
		const initialYear = yearsOptions.find(
			(year) => year.value === getYear(dateOfBirth)
		);
		const [currentDateOption, setCurrentDateOption] = useState(initialDate);
		const [currentMonthOption, setCurrentMonthOption] = useState(initialMonth);
		const [currentYearOption, setCurrentYearOption] = useState(initialYear);

		const [currentCancerAlliance, setCurrentCancerAlliance] = useState({});
		const [currentDiseaseGroup, setCurrentDiseaseGroup] = useState({});

		useEffect(() => {
			if (currentDateOption && currentMonthOption && currentDateOption) {
				const d = new Date(
					Date.UTC(
						currentYearOption.value,
						currentMonthOption.value,
						currentDateOption.value
					)
				);
				setDateOfBirth(d);
				submitResponse({ ...formData, dob: d.getTime() });
			}
		}, [currentDateOption, currentMonthOption, currentYearOption]);

		useEffect(() => {
			const d = secondsToMilliseconds(getUnixTime(dateOfBirth));
			let updatedData = { ...formData };
			if (d !== updatedData.dob) {
				updatedData.dob = d;
				setFormData(updatedData);
			}
		}, [dateOfBirth, formData]);

		useEffect(() => {
			if (
				currentDateOption &&
				currentMonthOption &&
				currentDateOption &&
				dayOptions
			) {
				const d = getDaysInMonth(
					new Date(Date.UTC(currentYearOption.value, currentMonthOption.value))
				);
				setDaysInMonth(d);
				if (currentDateOption && currentDateOption.value > d) {
					setCurrentDateOption(
						dayOptions.find((date) => {
							return date.value === 1;
						})
					);
				}
			}
		}, [currentMonthOption, currentYearOption, currentDateOption, dayOptions]);

		useEffect(() => {
			const text = (
				<>
					<p className="mb-2">
						There was a problem connecting to our server, probably due to the
						connection to the internet.
					</p>
					<p className="mb-2">
						Please press "Back" and try again when you are sure your connection
						is working.
					</p>
				</>
			);

			let aborted = false;

			getList("canceralliance", "", "Select the cancer alliance")
				.then((result) => {
					if (aborted) {
						return;
					}

					if (result.success) {
						const cas = [];

						for (const r of result.data.list) {
							cas.push({
								value: r.value,
								id: r.value,
								label: r.display,
							});
						}

						setCancerAlliances(cas);
					} else {
						toast(text, {
							hideProgressBar: true,
							type: "error",
						});
					}
				})
				.catch(() => {
					if (aborted) {
						return;
					}

					toast(text, {
						hideProgressBar: true,
						type: "error",
					});
				});

			return () => {
				aborted = true;
			};
		}, []);

		useEffect(() => {
			if (!def.showCancer) {
				return;
			}

			const text = (
				<>
					<p className="mb-2">
						There was a problem connecting to our server, probably due to the
						connection to the internet.
					</p>
					<p className="mb-2">
						Please press "Back" and try again when you are sure your connection
						is working.
					</p>
				</>
			);

			let aborted = false;

			getList("diseasegroup", "", "Select the type of cancer", def.showCancer)
				.then((result) => {
					if (aborted) {
						return;
					}

					if (result.success) {
						const dgs = [];

						for (const r of result.data.list) {
							dgs.push({
								value: r.value,
								id: r.value,
								label: r.cancerName || r.display,
							});
						}

						setDiseaseGroups(dgs);
					} else {
						toast(text, {
							hideProgressBar: true,
							type: "error",
						});
					}
				})
				.catch(() => {
					if (aborted) {
						return;
					}

					toast(text, {
						hideProgressBar: true,
						type: "error",
					});
				});

			return () => {
				aborted = true;
			};
		}, [def.showCancer]);

		useEffect(() => {
			if (!cancerAlliances) return;
			setCurrentCancerAlliance(
				cancerAlliances.find(
					(alliance) => alliance.value === data.cancerAlliance
				)
			);
		}, [data?.cancerAlliance, cancerAlliances]);

		useEffect(() => {
			if (!diseaseGroups || !def.showCancer) return;
			setCurrentDiseaseGroup(
				diseaseGroups.find((dg) => dg.value === data.diseaseGroup)
			);
		}, [data?.diseaseGroup, diseaseGroups, def]);

		const sexElements = sexOptions.map((e) => {
			const element = (
				<>
					<Field
						type="radio"
						name="sex"
						key={e.id}
						id={e.id}
						className={"hidden radioOption"}
						value={e.value}
						checked={formData.sex === e.value}
						onClick={() => {
							setFormField(e.value, "sex");
						}}
					/>
					<label
						htmlFor={e.id}
						className={`option ${
							formData.sex === e.value
								? `bg-black text-white font-medium`
								: "bg-gray-100"
						}`}
					>
						{e.label}
					</label>
				</>
			);

			return element;
		});

		const [phoneNumberValid, setPhoneNumberValid] = useState(true);
		const onChangePhoneNumber = (e) => {
			const value = e.target.value;
			if (value) {
				const check = phone(value);
				if (check.isValid === true) {
					setPhoneNumberValid(true);
				} else {
					const ukCheck = phone(value, { country: "GBR" });
					setPhoneNumberValid(ukCheck.isValid);
				}

				setFormField(sanitize(value), "mobilePhone");
			} else {
				setFormField("", "mobilePhone");
				setPhoneNumberValid(true);
			}
		};

		const onChangeCancerAlliance = (sel) => {
			setCurrentCancerAlliance(sel);
			setFormField(sel.value, "cancerAlliance");
		};

		const onChangeDiseaseGroup = (sel) => {
			setCurrentDiseaseGroup(sel);
			setFormField(sel.value, "diseaseGroup");
		};

		useEffect(() => {
			if (currentDiseaseGroup && def.showCancer) {
				setFormField(currentDiseaseGroup.label, "cancerName");
			}
		}, [currentDiseaseGroup, def]);

		const handleValidate = (valid) => {
			setValidatorIsValid(valid);
		};

		useEffect(() => {
			// submitResponse whenever valid state changes
			submitResponse(formData);
		}, [validatorIsValid]);

		return (
			<div className={"text-sm lg:text-base"}>
				<QuestionnaireHeader def={def} />
				<Form
					schema={schema}
					data={formData}
					errorMessages={{
						required: () => "You must fill out all required fields",
					}}
				>
					<div className={"max-w-lg overflow-y-visible h-fit mx-3 text-black"}>
						{def.showCancer && (
							<div className={"mb-4 text-sm"}>
								<label className={"basicFormLabel"} htmlFor={"dg"}>
									Cancer type<sup>*</sup>
								</label>
								<div className="w-full">
									<Field
										name="diseaseGroup"
										id="diseaseGroup"
										key="diseaseGroup"
										options={diseaseGroups}
										value={currentDiseaseGroup}
										onChange={onChangeDiseaseGroup}
										classes="w-full"
										component={SimpleSelect}
									/>
									<FieldError
										errorMessages={{
											required: () => "You must fill out all required fields",
										}}
										name={"diseaseGroup"}
										className={"rounded-b-sm z-0"}
									/>
								</div>
							</div>
						)}
						<div className={"mb-4 text-sm"}>
							<label className={"basicFormLabel"} htmlFor={"dg"}>
								Cancer alliance<sup>*</sup>
							</label>
							<div className="w-full">
								<Field
									name="cancerAlliance"
									id="cancerAlliance"
									key="cancerAlliance"
									options={cancerAlliances}
									value={currentCancerAlliance}
									onChange={onChangeCancerAlliance}
									classes="w-full"
									component={SimpleSelect}
								/>
							</div>
						</div>

						<div className={"mb-4 text-sm"}>
							<label className={"basicFormLabel"} htmlFor={"firstName"}>
								First Name<sup>*</sup>
							</label>
							<div className="w-full">
								<Field
									type="text"
									key="firstName"
									className={"formField z-0"}
									id={"firstName"}
									name="firstName"
									placeholder={"First Name"}
									value={formData.firstName}
									onChange={(e) => {
										setFormField(sanitize(e.target.value), "firstName");
									}}
								/>
								<FieldError
									errorMessages={{
										required: () => "You must fill out all required fields",
									}}
									name={"firstName"}
									className={"rounded-b-sm z-0"}
								/>
							</div>
						</div>

						<div className={"mb-4 text-sm"}>
							<label className={"basicFormLabel"} htmlFor={"surname"}>
								Surname<sup>*</sup>
							</label>
							<div className="w-full">
								<Field
									type="text"
									key="surname"
									className={"formField z-0"}
									id={"surname"}
									name="surname"
									placeholder={"Surname"}
									value={formData.surname}
									onChange={(e) => {
										setFormField(sanitize(e.target.value), "surname");
									}}
								/>
								<FieldError
									errorMessages={{
										required: () => "You must fill out all required fields",
									}}
									name={"surname"}
									className={"rounded-b-sm z-0"}
								/>
							</div>
						</div>

						<div className={"mb-4 text-sm"}>
							<label className={"basicFormLabel"} htmlFor={"dob"}>
								Date of Birth<sup>*</sup>
							</label>
							<div className="flex-grow  sm:gap-4 gap-2 flex">
								<div className="w-3/4">
									<Select
										options={dayOptions}
										selectedOption={currentDateOption}
										handleChange={(value) => {
											setCurrentDateOption(
												dayOptions.find((day) => day.id === value)
											);
										}}
										bg={"bg-gray-50"}
									/>
								</div>
								<div className="w-3/4">
									<Select
										options={monthsOptions}
										selectedOption={currentMonthOption}
										handleChange={(value) => {
											setCurrentMonthOption(
												monthsOptions.find((month) => month.id === value)
											);
										}}
										bg={"bg-gray-50"}
									/>
								</div>
								<div className="w-full">
									<Select
										options={yearsOptions}
										selectedOption={currentYearOption}
										handleChange={(value) => {
											setCurrentYearOption(
												yearsOptions.find((year) => year.id === value)
											);
										}}
										bg={"bg-gray-50"}
									/>
								</div>
							</div>
						</div>

						<div className={"mb-4 text-sm"}>
							<label className={"basicFormLabel"} htmlFor={"postalCode"}>
								Postcode<sup>*</sup>
							</label>
							<div className="w-full">
								<Field
									type="text"
									key="postalCode"
									className={"formField z-0"}
									id={"postalCode"}
									name="postalCode"
									placeholder={"Postcode"}
									value={formData.postalCode}
									onChange={(e) => {
										setFormField(sanitize(e.target.value), "postalCode");
									}}
								/>
								<FieldError
									errorMessages={{
										required: () => "You must fill out all required fields",
									}}
									name={"postalCode"}
									className={"rounded-b-sm z-0"}
								/>
							</div>
						</div>

						<div className={"mb-4 text-sm"}>
							<label className={"basicFormLabel"} htmlFor={"mobilePhone"}>
								Mobile No.
							</label>
							<div className="w-full">
								<input
									key="mobilePhone"
									type="text"
									className={"formField z-0"}
									id={"mobilePhone"}
									name="mobilePhone"
									placeholder={"Mobile Phone"}
									value={formData.mobilePhone}
									onChange={onChangePhoneNumber}
								/>
								{phoneNumberValid ? null : (
									<div className={"Jfv_FieldError isTouched rounded-sm z-0"}>
										Phone number is not valid
									</div>
								)}
							</div>
						</div>

						<div className={"mb-4 text-sm"}>
							<label className={"basicFormLabel"} htmlFor={"sex"}>
								Sex
							</label>
							<div className={"sm:flex mb-3"}>
								<div className={`radioSlider`}>{sexElements}</div>
							</div>
						</div>
					</div>
					<FormValidator validateCallback={handleValidate} />
				</Form>
				<QuestionnaireFooter def={def} />
			</div>
		);
	},
	areEqual
);
